import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { LeafletContextInterface } from './context';

export type LeafletElement<T, C = unknown> = Readonly<{
    instance: T
    context: LeafletContextInterface
    container?: C | null
}>;

export type ElementHook<E, P, C = unknown> = (props: P, context: LeafletContextInterface) => MutableRefObject<LeafletElement<E, C>>;

export function createElementObject<T, C = unknown>(instance: T, context: LeafletContextInterface, container?: C | null): LeafletElement<T, C> {
    return Object.freeze({
        instance,
        context,
        container
    });
}

function useInitializedElementRef<E, C>(createElement: () => LeafletElement<E, C>): MutableRefObject<LeafletElement<E, C>> {
    const elementRef = useRef<LeafletElement<E, C>>(null!);
    if (!elementRef.current) {
        elementRef.current = createElement();
    }
    return elementRef;
}

export function createElementHook<E, P, C = unknown>(createElement: (props: P, context: LeafletContextInterface) => LeafletElement<E, C>, updateElement?: (instance: E, props: P, prevProps: P) => void): ElementHook<E, P, C> {
    if (updateElement == null) {
        return function useImmutableLeafletElement(props, context) {
            return useInitializedElementRef(()=>createElement(props, context));
        };
    }
    return function useMutableLeafletElement(props, context) {
        const elementRef = useInitializedElementRef(()=>createElement(props, context));
        const propsRef = useRef(props);
        const { instance  } = elementRef.current;
        useEffect(function updateElementProps() {
            if (propsRef.current !== props) {
                updateElement(instance, props, propsRef.current);
                propsRef.current = props;
            }
        }, [
            instance,
            props,
            context
        ]);
        return elementRef;
    };
}
